import React, { useContext, useEffect } from 'react';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import SEO from 'react-seo-component';
import { hot } from 'react-hot-loader/root';
import { history } from '@src/history';
import { useAlert } from 'react-alert';
import { CachePersistor } from 'apollo-cache-persist';
import { InMemoryCache } from 'apollo-cache-inmemory';
import ReactPixel from 'react-facebook-pixel';
import useIsComDomain from '@src/hooks/useIsComDomain';

import { Loading } from '@atoms';
// import ZendeskService from "../hooks/ZendeskService";

import Routes from './routes/Routes';
import SnackBar from '@src/componentsNew/SnackBar/SnackBar';

import { ME_FOR_APP, SHOP_FOR_APP, CATEGORY_FOR_APP, ME_EMAIL_VERIFIED } from '@src/app/queries';
import { meForApp } from '@src/app/__generated__/meForApp';
import { shopForApp } from '@src/app/__generated__/shopForApp';
import { categoryForApp } from '@src/app/__generated__/categoryForApp';
import { meEmailVerified } from '@src/app/__generated__/meEmailVerified';
import { tokenCreate_tokenCreate } from '@src/providers/CredentialProvider/__generated__/tokenCreate';

import { CredentialContext, credentialType, ServiceWorkerContext, DeviceContext, deviceType } from '@providers';

import { accountLoginUrl } from '@src/app/routes';

const App: React.FC<RouteComponentProps> = ({ location }) => {
  const isComDomain = useIsComDomain();
  const alert = useAlert();

  const currentPath = location.pathname;
  const { isMobile } = useContext<deviceType>(DeviceContext);
  const {
    anonymousCart,
    mgsAnonymousCart,
    createAndSetAnonymousCart,
    currentUser,
    setCurrentCart,
    setCurrentUser,
  } = useContext<credentialType>(CredentialContext);
  const { updateAvailable } = useContext(ServiceWorkerContext);

  const { data } = useQuery<shopForApp>(SHOP_FOR_APP, {
    onCompleted: async (data) => {
      sessionStorage.setItem('codUpperLimit', data.shop.codAvailableUpperLimit.toLocaleString().toString());
      sessionStorage.setItem('codLowerLimit', data.shop.codAvailableLowerLimit.toLocaleString().toString());
      sessionStorage.setItem(
        'mgsMaximumOrderWeightValue',
        data.shop.mgsShippingMethod.maximumOrderWeight.value.toString(),
      );
      sessionStorage.setItem(
        'mgsMaximumOrderWeightUnit',
        data.shop.mgsShippingMethod.maximumOrderWeight.unit.toString(),
      );
      sessionStorage.setItem('mgsShippingPrices', JSON.stringify(data.shop.mgsShippingMethod.prices.edges));
      sessionStorage.setItem('reviewRewardAmount', JSON.stringify(data.shop.reviewRewardAmount));
      sessionStorage.setItem('reviewRewardAmountWithImage', JSON.stringify(data.shop.reviewRewardAmountWithImage));
      sessionStorage.setItem('firstImagePostRewardAmount', JSON.stringify(data.shop.firstImagePostRewardAmount));
      sessionStorage.setItem('firstReviewRewardAmount', JSON.stringify(data.shop.firstReviewRewardAmount));
      sessionStorage.setItem('purchaseLimit', JSON.stringify(data.shop.purchaseLimit));
      sessionStorage.setItem('shopName', data.shop.name);
      sessionStorage.setItem('headerText', data.shop.headerText);
      sessionStorage.setItem('description', data.shop.description);
      sessionStorage.setItem('domainUrl', data.shop.domain.url);
    },
  });

  const [categoryForApp] = useLazyQuery<categoryForApp>(CATEGORY_FOR_APP, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      localStorage.setItem('category', JSON.stringify(data.categories.edges));
    },
  });

  useEffect(() => {
    categoryForApp();
  }, []);

  const [meForApp] = useLazyQuery<meForApp>(ME_FOR_APP, {
    fetchPolicy: 'no-cache',
    onCompleted: async (data) => {
      const maccaronCart = data?.me?.maccaronCart;
      const mgsCart = data?.me?.mgsCart;

      // user에 모든 cart가 존재
      if (maccaronCart && mgsCart) {
        // local Storage user 정보 업데이트
        const copyCurrentUser: tokenCreate_tokenCreate = JSON.parse(JSON.stringify(currentUser));
        copyCurrentUser.user.zendeskToken = data.me.zendeskToken;
        copyCurrentUser.user.maccaronCart = maccaronCart;
        copyCurrentUser.user.mgsCart = mgsCart;

        setCurrentUser(copyCurrentUser);
      } else {
        // 각 cart가 없을 경우 cart 생성
        if (!maccaronCart) {
          await createAndSetAnonymousCart({ forMgs: false }, true);
        }

        if (!mgsCart) {
          await createAndSetAnonymousCart({ forMgs: true }, true);
        }
      }
    },
  });

  const [meEmailVerified] = useLazyQuery<meEmailVerified>(ME_EMAIL_VERIFIED, {
    fetchPolicy: 'no-cache',
    onCompleted: async (data) => {
      const emailVerified = data?.me?.emailVerified;

      // local Storage user 정보 업데이트
      const copyCurrentUser: tokenCreate_tokenCreate = JSON.parse(JSON.stringify(currentUser));
      copyCurrentUser.user.emailVerified = emailVerified;

      setCurrentUser(copyCurrentUser);
    },
  });

  useEffect(() => {
    // 로그인 X
    if (!currentUser?.token) {
      if (!anonymousCart || anonymousCart?.forMgs === undefined) {
        createAndSetAnonymousCart({ forMgs: false }, false);
      }

      if (!mgsAnonymousCart || mgsAnonymousCart?.forMgs === undefined) {
        createAndSetAnonymousCart({ forMgs: true }, false);
      }
    }

    // 로그인 O
    if (currentUser?.token && !anonymousCart && !mgsAnonymousCart) {
      if (!currentUser?.user?.maccaronCart?.token || !currentUser?.user?.mgsCart?.token) {
        meForApp();
      }

      if (currentUser?.user?.emailVerified === undefined) {
        meEmailVerified();
      }
    }
  }, [JSON.stringify(anonymousCart), JSON.stringify(mgsAnonymousCart), JSON.stringify(currentUser)]);

  useEffect(() => {
    // Meta Pixel Page View
    ReactPixel.pageView();
  }, [currentPath]);

  // useEffect(() => {
  //   if (currentUser) {
  //     if (currentUser.user.zendeskToken) {
  //       ZendeskService.login(currentUser.user.zendeskToken);
  //     } else {
  //       meForApp();
  //     }
  //   } else {
  //     ZendeskService.logout();
  //   }
  // }, []);

  history.listen((location, action) => {
    //@ts-ignore: WTF
    alert.removeAll();
  });

  const cache = new InMemoryCache();
  const persistor = new CachePersistor({
    cache,
    storage: window.localStorage,
  });

  useEffect(() => {
    if (updateAvailable) {
      alert.show('New version is available! ' + '(Page will be refreshed after 5 seconds)', {
        onClose: () => {
          window.location.reload();
          persistor.purge();
        },
        timeout: 5000,
        type: 'success',
      });
    }
  }, [updateAvailable]);

  const pathsRequireCredential = [
    '/en/account/',
    '/en/account/orders/',
    '/en/account/reviews/',
    '/en/account/mypoints/',
    '/en/account/share/',
    '/en/account/change-password/',
    '/en/account/contacts/',
    '/en/account/delete-view/',
    '/en/account/pending-reviews/',
    '/en/account/wish-list/',
  ];
  const redirectPath = pathsRequireCredential.includes(currentPath);

  const regPathRequireCredential = [/\/en\/account\/.+\/delete-confirm\//];
  const redirectRegPath = regPathRequireCredential.some((r) => currentPath.match(r));

  const redirectToLogin = !currentUser?.token && (redirectPath || redirectRegPath);

  if (redirectToLogin) {
    return (
      <Redirect
        to={{
          pathname: accountLoginUrl,
          state: { from: { pathname: currentPath } },
        }}
      />
    );
  } else {
    return (
      (data && (
        <>
          <SEO
            title={data.shop.headerText}
            titleTemplate={data.shop.name}
            titleSeparator={`-`}
            description={data.shop.description}
            image="/static/images/block1.png"
            pathname={data.shop.domain.url}
            siteLanguage="en"
            siteLocale="en_GB"
            twitterUsername=""
          />
          <Routes isMobile={isMobile} />

          <SnackBar />
        </>
      )) || <Loading />
    );
  }
};

export default hot(withRouter(App));
